import React, { useState } from "react";
import Modal from "./Modal.tsx";
import InputField from "../Forms/InputField.js";
import { useForm } from "react-hook-form";
import SelectField from "../Forms/SelectField.js";
import TextAreaField from "../Forms/TextAreaField.js";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FileUpload from "../FileUpload.js";
import { documentUpload, uploadFileToSignedUrl } from "../../services/util.js";
import { dateFormat } from "../../utils/functions.js";
import toast from "react-hot-toast";

export default function UploadModal({
  isModalOpen,
  setIsModalOpen,
  userType,
  userId,
  refreshData,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDocuments, setUploadDocuments] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [fileNames, setFileNames] = useState([]);

  const documentOptions = [
    { label: "Utility Bill", value: "Utility Bill" },
    { label: "Others", value: "Others" },
  ];
  const trainingDocumentOptions = [
    { label: "Dementia", value: "Dementia" },
    { label: "Medication Awareness", value: "Medication Awareness" },
    { label: "Fire training", value: "Fire training" },
    { label: "Basic Life Support", value: "Basic Life Support" },
    { label: "First Aid Awareness", value: "First Aid Awareness" },
    { label: "Food Hygiene", value: "Food Hygiene" },
    { label: "Epilepsy Awareness", value: "Epilepsy Awareness" },
    { label: "Health and Safety", value: "Health and Safety" },
    { label: "Infection Control", value: "Infection Control" },
    { label: "Mental Capacity", value: "Mental Capacity" },
    { label: "Risk Assessment", value: "Risk Assessment" },
    { label: "Moving and Handling", value: "Moving and Handling" },
    {
      label: "Safeguarding of vulnerable adults/children",
      value: "Safeguarding of vulnerable adults/children",
    },
    {
      label: "Understanding learning disabilities",
      value: "Understanding learning disabilities",
    },
  ];

  const handleDocumentSelected = (files) => {
    const newFiles = Array.from(files);
    setUploadDocuments((prevUploadDocuments) => [
      ...prevUploadDocuments,
      ...newFiles,
    ]);

    // Initialize file names state with original file names
    const initialFileNames = {};
    newFiles.forEach((file, index) => {
      initialFileNames[file.name] = file.name;
    });
    setFileNames((prevFileNames) => ({
      ...prevFileNames,
      ...initialFileNames,
    }));
  };

  const getFileExtension = (fileName) => {
    // Split the file name by '.' and get the last part
    return fileName.split(".").pop();
  };

  const getFileNameWithoutExtension = (fileName) => {
    return fileName.includes(".")
      ? fileName.substring(0, fileName.lastIndexOf("."))
      : fileName;
  };

  const handleFileNameChange = (fileName, newName) => {
    const extension = getFileExtension(fileName);

    // Remove extension if the new name already contains it
    const cleanedNewName = newName.replace(new RegExp(`\\.${extension}$`), "");

    setFileNames((prevFileNames) => ({
      ...prevFileNames,
      [fileName]: cleanedNewName,
    }));
  };

  const getUpdatedFileNames = () => {
    return uploadDocuments.map((file) => {
      const originalExtension = getFileExtension(file.name);
      const newName = fileNames[file.name] || file.name;

      // Ensure extension is added only if missing
      return newName.endsWith(`.${originalExtension}`)
        ? newName
        : `${newName}.${originalExtension}`;
    });
  };

  const validationSchema = Yup.object().shape({
    groupName: Yup.string().required("Document group is required"),
    expirationDate: Yup.date().required("Expiry date is required"),
  });

  const extractFileNameFromError = (errorMessage) => {
    const fileNameRegex = /[^/]+\/([^/]+\.\w{3,4})\)/;
    const match = errorMessage.match(fileNameRegex);

    if (match && match[1]) {
      const fileKey = match[1]; // Extract the file_key
      const fileName = fileKey.split("/").pop(); // Extract the file name from the file_key
      return fileName;
    } else {
      throw new Error("File name not found in the error message.");
    }
  };

  const handleFormSubmit = async (data) => {
    if (uploadDocuments.length === 0) {
      setUploadError("Please upload at least one document");
      return;
    }

    setUploadError("");
    setIsLoading(true);

    const payload = {
      groupName: data.groupName,
      expirationDate: dateFormat(data.expirationDate, "yyyy-MM-dd HH:mm:ss"),
      comments: data.comments,
      fileNames: getUpdatedFileNames(), // Use the inputted file names
      userType: userType,
      userId: userId,
    };

    try {
      // Step 1: Get signed URLs from the API
      const response = await documentUpload(payload);
      const uploadUrls = response.data.upload_urls;

      // Step 2: Match files with their signed URLs using the inputted file names
      const uploadPromises = uploadDocuments.map((file) => {
        const inputtedFileName = `${fileNames[file.name]}`; // Get the inputted file name
        const signedUrlData = uploadUrls.find(
          (urlData) =>
            getFileNameWithoutExtension(urlData.file_name) ===
            getFileNameWithoutExtension(inputtedFileName) // Match with the inputted file name
        );

        if (!signedUrlData)
          throw new Error(`No signed URL found for file: ${inputtedFileName}`);

        // Step 3: Upload the file to the signed URL
        return uploadFileToSignedUrl(file, signedUrlData.upload_url);
      });

      // Step 4: Run all uploads concurrently
      await Promise.all(uploadPromises);

      toast.success("Files uploaded successfully");
    } catch (error) {
      console.log("Error uploading files:", error);
      if (error?.response?.data.includes("duplicate")) {
        const fileName = extractFileNameFromError(error.response.data);
        toast.error(`File with name ${fileName} already exists`);
      } else {
        toast.error("Failed to upload files. Please try again.");
      }
    } finally {
      refreshData();
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  return (
    <Modal
      title="Upload documents"
      onCancel={() => setIsModalOpen(false)}
      isOpen={isModalOpen}
      size="lg"
    >
      <form onSubmit={handleSubmit((data) => handleFormSubmit(data))}>
        <InputField
          name="expirationDate"
          control={control}
          label="Expiry Date"
          type="date"
          error={errors["expirationDate"]?.message}
          required
        />
        <SelectField
          label="Document group"
          options={
            userType === "staff" ? trainingDocumentOptions : documentOptions
          }
          control={control}
          name="groupName"
          error={errors.groupName?.message}
          required
        />
        <TextAreaField
          name="comments"
          control={control}
          label="Comments"
          error={errors["comments"]?.message}
          required
        />
        <div className="mb-16">
          <FileUpload onFilesSelected={handleDocumentSelected} />
        </div>
        {uploadError && <span className="form__error">{uploadError}</span>}
        {uploadDocuments.length > 0 && (
          <div className="form__section">
            <div className="mb-16">
              <p className="section--subheader">
                Please enter the name of the documents you have uploaded.
              </p>
            </div>
            <div className="form__section-wrap">
              {uploadDocuments.map((file, index) => (
                <div className="form__group" key={index}>
                  <label>{file.name}</label>
                  <div className="form__input">
                    <input
                      type="text"
                      value={fileNames[file.name] || ""}
                      onChange={(e) =>
                        handleFileNameChange(file.name, e.target.value)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          type="submit"
          className="btn btn__primary btn--block"
          disabled={isLoading}
        >
          {isLoading ? "Loading" : "Upload"}
        </button>
      </form>
    </Modal>
  );
}
